import axios from 'axios';
import type { DraftAttribution, VisionSummary } from './visionHandoff';

export type VisionApiResponse = {
  idea: string;
  summary: VisionSummary | null;
  error?: string;
};

export type VisionApiRequest = {
  content: string;
  draftToken: string;
  attribution: DraftAttribution;
};

export async function generateVisionSummary(
  { content, draftToken, attribution }: VisionApiRequest,
  signal?: AbortSignal
): Promise<VisionApiResponse> {
  const { data } = await axios.post<VisionApiResponse>(
    '/api/landing/vision',
    // `draft_token` keys the anonymous draft row on the same token the funnel
    // events carry; `attribution` is stored beside it so a claimed draft still
    // says where the visitor came from.
    { content, draft_token: draftToken, attribution },
    { signal, timeout: 50_000 }
  );
  return data;
}

/**
 * Asks the server for the token this visit is recorded against.
 *
 * The server issues it rather than accepting one the browser made up, because that
 * token is what marks a product as having come through `/start` — and with it, the free
 * onboarding journey. See `OnboardingDraftsController`.
 */
export async function createDraftToken(
  signal?: AbortSignal
): Promise<string | null> {
  try {
    const { data } = await axios.post<{ token?: string }>(
      '/api/onboarding/drafts',
      {},
      { signal, timeout: 10_000 }
    );
    return data?.token ?? null;
  } catch {
    // A visitor the server could not issue a draft to can still read the page, still
    // generate a vision and still sign up; what they lose is the flow on the other
    // side. Silent on purpose: there is nothing for them to do about it.
    return null;
  }
}
